import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Download, Loader2, Music, Film, CheckCircle2, Volume2, Gauge, Stamp, Lock, Crown, Upload, Type, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { runDownload } from "@/lib/download";
import { useLang } from "@/lib/i18n";
import { formatDuration, formatSpeed, formatEta } from "@/lib/utils-vg";

const QUALITY_OPTIONS = [
  { id: "best", label: "Best", sub: "Auto", testid: "quality-option-best", icon: Film },
  { id: "1080", label: "1080p", sub: "FHD", testid: "quality-option-1080p", icon: Film },
  { id: "720", label: "720p", sub: "HD", testid: "quality-option-720p", icon: Film },
  { id: "480", label: "480p", sub: "SD", testid: "quality-option-480p", icon: Film },
  { id: "audio", label: "MP3", sub: "Audio", testid: "quality-option-audio-mp3", icon: Music },
];

export const VideoPreview = ({ info, onDownloaded, requireAuth, onLimitReached, isPremium, onUpgrade }) => {
  const { t } = useLang();
  const [quality, setQuality] = useState("best");
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  const [speed, setSpeed] = useState(null);
  const [eta, setEta] = useState(null);
  const [watermarkOn, setWatermarkOn] = useState(false);
  const [wmMode, setWmMode] = useState("text"); // text | logo
  const [watermarkText, setWatermarkText] = useState("");
  const [watermarkPos, setWatermarkPos] = useState("bottom-right");
  const [watermarkLogo, setWatermarkLogo] = useState(null); // data URL
  const [wmOpacity, setWmOpacity] = useState(0.9);
  const [wmSize, setWmSize] = useState("medium");
  const [wmColor, setWmColor] = useState("#ffffff");

  const available = info.available_qualities || [];
  const options = QUALITY_OPTIONS.filter((o) => o.id === "best" || o.id === "audio" || available.includes(o.id));
  const isAudio = quality === "audio";

  const handleDownload = async () => {
    if (requireAuth && requireAuth()) return;
    setDownloading(true);
    setProgress(0);
    setStage("queued");
    setSpeed(null);
    setEta(null);
    try {
      const useWm = isPremium && watermarkOn && !isAudio;
      await runDownload({
        url: info.webpage_url,
        quality,
        watermarkText: useWm && wmMode === "text" ? watermarkText : null,
        watermarkLogo: useWm && wmMode === "logo" ? watermarkLogo : null,
        watermarkPosition: watermarkPos,
        watermarkOpacity: wmOpacity,
        watermarkSize: wmSize,
        watermarkColor: wmColor,
        onProgress: (p) => {
          setProgress(Math.round(p.percent || 0));
          setStage(p.status || "downloading");
          setSpeed(p.speed);
          setEta(p.eta);
        },
      });
      toast.success(t("toast.downloadComplete"));
      onDownloaded && onDownloaded();
    } catch (e) {
      if (e.limit) {
        onLimitReached && onLimitReached();
      } else {
        toast.error(e.message || t("toast.downloadFail"));
      }
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setProgress(0);
        setStage("");
        setSpeed(null);
        setEta(null);
      }, 700);
    }
  };

  const stageKey = ["queued", "downloading", "processing", "saving"].includes(stage) ? `stage.${stage}` : "stage.downloading";

  const isVertical = false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-3xl mx-auto mt-8 vg-glass rounded-2xl p-5 sm:p-6 relative overflow-hidden"
      data-testid="video-preview-card"
    >
      <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />

      <div className="flex items-center gap-2 mb-5">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          <CheckCircle2 size={13} /> {t("prev.detected")}
        </span>
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500" data-testid="platform-badge">
          {info.extractor}
        </span>
      </div>

      <div className="grid sm:grid-cols-[200px,1fr] gap-5">
        <div className={`relative rounded-xl overflow-hidden bg-black/40 border border-white/10 ${isVertical ? "aspect-[9/16]" : "aspect-video"}`}>
          {info.thumbnail ? (
            <img src={info.thumbnail} alt={info.title} className="w-full h-full object-cover" data-testid="video-thumbnail-image" />
          ) : (
            <div className="w-full h-full grid place-items-center text-slate-600">
              <Film size={32} />
            </div>
          )}
          <div className="absolute inset-0 grid place-items-center">
            <div className="h-11 w-11 rounded-full bg-black/50 backdrop-blur grid place-items-center border border-white/20">
              <Play size={18} className="text-white fill-white ml-0.5" />
            </div>
          </div>
          <span
            className="absolute bottom-2 right-2 text-[11px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-white"
            data-testid="video-duration-badge"
          >
            {formatDuration(info.duration)}
          </span>
        </div>

        <div className="min-w-0">
          <h3 className="text-lg font-head font-semibold leading-snug line-clamp-2" data-testid="video-title-heading">
            {info.title}
          </h3>
          <p className="text-sm text-slate-400 mt-1 truncate">{info.uploader}</p>

          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-lg">
            <Volume2 size={13} /> {t("prev.audioIncluded")}
          </div>

          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-5 mb-2">{t("prev.selectQuality")}</p>
          <div className="flex flex-wrap gap-2">
            {options.map((o) => {
              const Icon = o.icon;
              const active = quality === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => setQuality(o.id)}
                  disabled={downloading}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border transition-all ${
                    active
                      ? "bg-gradient-to-r from-violet-600/30 to-cyan-500/20 border-violet-500/60 text-white"
                      : "bg-black/30 border-white/10 text-slate-300 hover:border-white/25"
                  }`}
                  data-testid={o.testid}
                >
                  <Icon size={14} className={active ? "text-violet-300" : "text-slate-500"} />
                  <span className="font-medium">{o.label}</span>
                  <span className="text-[10px] text-slate-500">{o.sub}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6">
        {!isAudio && (
          !isPremium ? (
            <div
              className="mb-4 rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 to-transparent p-4 flex items-center gap-3"
              data-testid="watermark-locked"
            >
              <div className="h-9 w-9 rounded-lg bg-amber-500/20 grid place-items-center shrink-0">
                <Lock size={16} className="text-amber-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white">{t("wm.lockTitle")}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t("wm.lockDesc")}</p>
              </div>
              <button
                type="button"
                onClick={onUpgrade}
                className="shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 text-sm font-semibold text-black hover:opacity-95 transition-opacity"
                data-testid="watermark-upgrade-button"
              >
                <Crown size={14} /> {t("nav.premium")}
              </button>
            </div>
          ) : (
            <div className="mb-4 rounded-xl border border-white/10 bg-black/20 p-3.5" data-testid="watermark-panel">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="flex items-center gap-2 text-sm text-slate-200">
                  <Stamp size={15} className="text-cyan-400" /> {t("wm.title")}
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">PRO</span>
                </span>
                <button
                  type="button"
                  onClick={() => setWatermarkOn((v) => !v)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${watermarkOn ? "bg-violet-600" : "bg-white/15"}`}
                  data-testid="watermark-toggle"
                  aria-pressed={watermarkOn}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${watermarkOn ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>

              {watermarkOn && (
                <div className="mt-3 space-y-3">
                  {/* mode tabs */}
                  <div className="flex gap-1 p-1 rounded-lg bg-black/30 border border-white/5">
                    {[
                      { id: "text", label: t("wm.text"), icon: Type },
                      { id: "logo", label: t("wm.logo"), icon: ImageIcon },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setWmMode(m.id)}
                        className={`flex-1 py-1.5 rounded-md text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                          wmMode === m.id ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white" : "text-slate-400"
                        }`}
                        data-testid={`watermark-mode-${m.id}`}
                      >
                        <m.icon size={13} /> {m.label}
                      </button>
                    ))}
                  </div>

                  {wmMode === "text" ? (
                    <>
                      <input
                        value={watermarkText}
                        onChange={(e) => setWatermarkText(e.target.value)}
                        maxLength={40}
                        placeholder={t("wm.placeholder")}
                        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-violet-500/50"
                        data-testid="watermark-input"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-500 w-12">{t("wm.color")}</span>
                        {["#ffffff", "#000000", "#ef4444", "#f59e0b", "#22d3ee", "#a78bfa"].map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setWmColor(c)}
                            style={{ background: c }}
                            className={`h-6 w-6 rounded-full border-2 transition-transform ${wmColor === c ? "border-white scale-110" : "border-white/20"}`}
                            data-testid={`watermark-color-${c.slice(1)}`}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <label
                      className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-white/20 bg-black/30 cursor-pointer hover:border-violet-500/40 transition-colors"
                      data-testid="watermark-logo-dropzone"
                    >
                      {watermarkLogo ? (
                        <img src={watermarkLogo} alt="logo" className="h-10 w-10 object-contain rounded bg-white/10" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-white/5 grid place-items-center">
                          <Upload size={16} className="text-slate-400" />
                        </div>
                      )}
                      <span className="text-sm text-slate-300">{watermarkLogo ? t("wm.logoReady") : t("wm.uploadLogo")}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="watermark-logo-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 3_000_000) {
                            toast.error(t("wm.logoTooLarge"));
                            return;
                          }
                          const r = new FileReader();
                          r.onload = () => setWatermarkLogo(r.result);
                          r.readAsDataURL(f);
                        }}
                      />
                    </label>
                  )}

                  {/* position */}
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: "top-left", label: t("wm.topLeft") },
                      { id: "top-right", label: t("wm.topRight") },
                      { id: "bottom-left", label: t("wm.bottomLeft") },
                      { id: "bottom-right", label: t("wm.bottomRight") },
                      { id: "center", label: t("wm.center") },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setWatermarkPos(p.id)}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border transition-all ${
                          watermarkPos === p.id
                            ? "bg-violet-600/30 border-violet-500/60 text-white"
                            : "bg-black/30 border-white/10 text-slate-300 hover:border-white/25"
                        }`}
                        data-testid={`watermark-pos-${p.id}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  {/* size + opacity */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[11px] text-slate-500 w-12">{t("wm.size")}</span>
                    {["small", "medium", "large"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setWmSize(s)}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border transition-all capitalize ${
                          wmSize === s ? "bg-violet-600/30 border-violet-500/60 text-white" : "bg-black/30 border-white/10 text-slate-300"
                        }`}
                        data-testid={`watermark-size-${s}`}
                      >
                        {t(`wm.${s}`)}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-slate-500 w-12">{t("wm.opacity")}</span>
                    <input
                      type="range"
                      min="0.3"
                      max="1"
                      step="0.1"
                      value={wmOpacity}
                      onChange={(e) => setWmOpacity(parseFloat(e.target.value))}
                      className="flex-1 accent-violet-500"
                      data-testid="watermark-opacity"
                    />
                    <span className="text-xs font-mono text-slate-400 w-10 text-right">{Math.round(wmOpacity * 100)}%</span>
                  </div>
                  <p className="text-[11px] text-slate-500">{t("wm.hint")}</p>
                </div>
              )}
            </div>
          )
        )}
        {downloading && (
          <div className="mb-3">
            <div className="flex justify-between items-center text-xs mb-1.5">
              <span className="text-slate-400 font-mono flex items-center gap-2" data-testid="download-status-text">
                {t(stageKey)}
                {speed ? (
                  <span className="inline-flex items-center gap-1 text-cyan-400">
                    <Gauge size={12} /> {formatSpeed(speed)}
                  </span>
                ) : null}
                {eta != null && stage === "downloading" ? (
                  <span className="text-slate-500">{t("label.eta")} {formatEta(eta)}</span>
                ) : null}
              </span>
              <span className="text-violet-400 font-mono font-semibold">{progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-black/40 overflow-hidden" data-testid="download-progress-bar">
              <motion.div
                className="h-full bg-gradient-to-r from-violet-500 to-cyan-400"
                animate={{ width: `${progress}%` }}
                transition={{ ease: "easeOut" }}
              />
            </div>
          </div>
        )}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-95 transition-opacity disabled:opacity-70 vg-pulse"
          data-testid="download-start-button"
        >
          {downloading ? <Loader2 size={18} className="vg-spin" /> : <Download size={18} />}
          {downloading ? t("prev.downloading") : quality === "audio" ? t("prev.downloadAudio") : t("prev.downloadVideo")}
        </button>
      </div>
    </motion.div>
  );
};
